// pages/LogicalCanvasSites.jsx
// Logical Canvas — Sites Management (Scope Definition)
// Sites define which system is being governed. They do NOT grant permissions or expose data.
// They ONLY define scope for plugins and policies.

import React from "react";

const AVAILABLE_SITES = [
  {
    id: "finance",
    name: "Finance",
    icon: "💰",
    description: "Financial accounting, ledger, and reporting capabilities",
    status: "active",
  },
  {
    id: "sales",
    name: "Sales",
    icon: "📊",
    description: "Sales pipeline, opportunities, and forecasting",
    status: "available",
  },
  {
    id: "crm",
    name: "CRM",
    icon: "👥",
    description: "Customer relationship management and contacts",
    status: "available",
  },
  {
    id: "marketing",
    name: "Marketing",
    icon: "📢",
    description: "Marketing campaigns, leads, and analytics",
    status: "available",
  },
  {
    id: "security",
    name: "Security",
    icon: "🔒",
    description: "Access control, encryption, and compliance",
    status: "available",
  },
];

export default function LogicalCanvasSites({ activeSite, onSiteChange }) {
  return (
    <div className="space-y-6">
      <div className="surface rounded-hard p-6">
        <h2 className="text-ice text-xl font-semibold mb-2">Sites (System Scope)</h2>
        <p className="text-mist/80 mb-4">
          Select which system is being governed. Sites define scope for plugins and policies.
          They do NOT grant permissions or expose data directly.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {AVAILABLE_SITES.map((site) => (
            <button
              key={site.id}
              onClick={() => onSiteChange(site.id)}
              className={`text-left p-5 rounded-lg border transition-all ${
                activeSite === site.id
                  ? "bg-[#1a2c36] border-ice shadow-lg"
                  : "surface border-edge hover:border-ice/50 hover:bg-[#0e1b22]"
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <span className="text-4xl">{site.icon}</span>
                {activeSite === site.id && (
                  <span className="px-2 py-1 rounded text-xs bg-ice/20 text-ice font-semibold">
                    ACTIVE
                  </span>
                )}
              </div>
              
              <h3 className="text-ice font-semibold text-lg mb-1">{site.name}</h3>
              <p className="text-mist/70 text-sm">{site.description}</p>
              
              <div className="mt-3 pt-3 border-t border-edge/50">
                <span className={`text-xs ${
                  site.status === "active" ? "text-[#7fd199]" : "text-mist/60"
                }`}>
                  {site.status === "active" ? "● System Active" : "○ Available"}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="surface rounded-hard p-6">
        <h3 className="text-ice font-semibold mb-3">What is a Site?</h3>
        <div className="space-y-3 text-sm">
          <div className="border-l-2 border-ice/30 pl-4">
            <div className="text-ice font-medium mb-1">Scope Definition</div>
            <p className="text-mist/70">
              A Site represents the system being governed (Finance, Sales, CRM, etc.).
              It defines the scope for plugins and policies.
            </p>
          </div>
          
          <div className="border-l-2 border-edge pl-4">
            <div className="text-ice font-medium mb-1">Zero Trust Boundary</div>
            <p className="text-mist/70">
              Sites cannot access each other directly. No cross-site database access.
              All inter-site communication requires approved artifacts.
            </p>
          </div>
          
          <div className="border-l-2 border-edge pl-4">
            <div className="text-ice font-medium mb-1">Authority Layer</div>
            <p className="text-mist/70">
              Logical Canvas sits ABOVE all sites. It governs but does not execute.
              Sites define scope. Plugins define capabilities. Policies define enforcement.
            </p>
          </div>
        </div>
      </div>

      <div className="surface rounded-hard p-5 bg-[#0a1318] border border-edge/50">
        <div className="flex items-start gap-3">
          <span className="text-2xl">ℹ️</span>
          <div>
            <h4 className="text-ice font-semibold mb-1">Current Selection</h4>
            <p className="text-mist/80 text-sm">
              {activeSite ? (
                <>
                  Governing: <span className="text-ice font-semibold">{AVAILABLE_SITES.find(s => s.id === activeSite)?.name}</span>
                  {" "}— Plugins and Policies are scoped to this system.
                </>
              ) : (
                "No site selected. Please select a site to view plugins and policies."
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
